// ============================================================================
// FSA CONSOLIDATED PRINT SCREEN — Phase 2 Report Builder (rail-tab variant)
// ============================================================================
// Auditor picks which Statements + Notes to include in the consolidated PDF
// and picks the display mode (Actual / Rounded). "Generate PDF" calls the
// backend (POST /api/v1/FsaConsolidatedPrint/by-engagement/{id}/preview-pdf)
// and streams the PDF blob into an iframe on the right pane.
//
// Layout — two-pane split:
//   · Left  (configuration)  — Statements list, Notes list, Mode toggle,
//                              Generate / Download / Open-Print buttons.
//   · Right (preview)        — PDF iframe; empty-state hint before generate,
//                              spinner while generating, error message on
//                              failure.
//
// Lives under the Reports group in the FSA workspace rail; replaces the
// earlier modal trigger from the engagement header. Notes excluded by the
// engagement-level "Do not include in note list" flag are hidden; the
// statement / note ordering follows template display_order and saved
// note_number respectively (the backend renderer trusts the order).

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import { useFsaStatementList } from 'hooks/api/fsa/useFsaStatements';
import { useFsaNotesList } from 'hooks/api/fsa/useFsaNotes';
import { fsaConsolidatedPrintService } from 'api/services/fsa/fsaConsolidatedPrintService';
import notificationService from 'services/notificationService';
import type { ID } from 'api/types';

interface Props {
  engagementId: ID;
}

// The Cash Flow Statement defaults to Not Applicable (the auditor opts in via
// Mark Applicable / by generating it), so an un-generated CF is excluded from
// the consolidated print by default — consistent with the Statement screen and
// the engagement home. An explicit isExcluded always wins.
const isStatementExcluded = (s: { reportCode: string; isExcluded: boolean; hasSavedData: boolean }): boolean =>
  s.isExcluded || (s.reportCode === 'CF' && !s.hasSavedData);

const FsaConsolidatedPrintScreen: React.FC<Props> = ({ engagementId }) => {
  const { data: statements, loading: stmtLoading } = useFsaStatementList(engagementId);
  const { data: notes, loading: notesLoading } = useFsaNotesList(engagementId);

  const [mode, setMode] = useState<'actual' | 'rounded'>('actual');
  const [selectedReportIds, setSelectedReportIds] = useState<Set<ID>>(new Set());
  const [selectedNoteIds, setSelectedNoteIds] = useState<Set<ID>>(new Set());

  const [generating, setGenerating] = useState(false);
  const [blobUrl, setBlobUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const orderedStatements = useMemo(
    () => [...(statements ?? [])].sort((a, b) => a.displayOrder - b.displayOrder),
    [statements],
  );
  const orderedNotes = useMemo(() => {
    return [...(notes ?? [])]
      .filter(n => !n.isExcluded)
      .sort((a, b) => {
        const an = a.noteNumber ?? Number.POSITIVE_INFINITY;
        const bn = b.noteNumber ?? Number.POSITIVE_INFINITY;
        if (an !== bn) return an - bn;
        return a.displayOrder - b.displayOrder;
      });
  }, [notes]);

  // Pre-select defaults whenever the lists arrive. Reset on engagement
  // change so switching engagements doesn't carry the prior selection.
  useEffect(() => {
    setSelectedReportIds(new Set(orderedStatements.filter(s => !isStatementExcluded(s)).map(s => s.reportId)));
    setSelectedNoteIds(new Set(orderedNotes.map(n => n.scheduleId)));
  }, [orderedStatements, orderedNotes]);

  // Clean up the previous PDF blob URL when a new one replaces it or the
  // screen unmounts — avoids leaking object URLs in memory.
  useEffect(() => {
    return () => {
      if (blobUrl) URL.revokeObjectURL(blobUrl);
    };
  }, [blobUrl]);

  const toggleReport = useCallback((id: ID) => {
    setSelectedReportIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }, []);
  const toggleNote = useCallback((id: ID) => {
    setSelectedNoteIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }, []);
  const toggleAllStatements = useCallback((checked: boolean) => {
    setSelectedReportIds(checked
      ? new Set(orderedStatements.map(s => s.reportId))
      : new Set());
  }, [orderedStatements]);
  const toggleAllNotes = useCallback((checked: boolean) => {
    setSelectedNoteIds(checked
      ? new Set(orderedNotes.map(n => n.scheduleId))
      : new Set());
  }, [orderedNotes]);

  const canGenerate = !generating
    && (selectedReportIds.size > 0 || selectedNoteIds.size > 0);

  const handleGenerate = useCallback(async () => {
    if (!engagementId || !canGenerate) return;
    setGenerating(true);
    setError(null);
    // Revoke the prior URL eagerly so the iframe doesn't keep displaying
    // a stale binary while the next one is fetching.
    if (blobUrl) URL.revokeObjectURL(blobUrl);
    setBlobUrl(null);
    try {
      const reportIds = orderedStatements
        .filter(s => selectedReportIds.has(s.reportId))
        .map(s => s.reportId);
      const noteScheduleIds = orderedNotes
        .filter(n => selectedNoteIds.has(n.scheduleId))
        .map(n => n.scheduleId);
      const blob = await fsaConsolidatedPrintService.getPreviewPdfBlob(engagementId, {
        reportIds, noteScheduleIds, mode,
      });
      const url = URL.createObjectURL(blob);
      setBlobUrl(url);
    } catch (err: any) {
      let message: string | null = null;
      const data = err?.response?.data;
      if (data instanceof Blob) {
        try {
          const text = await data.text();
          const parsed = JSON.parse(text);
          message = parsed?.message ?? null;
        } catch { /* leave null */ }
      } else if (typeof data === 'string') {
        message = data;
      } else if (data?.message) {
        message = data.message;
      }
      setError(message || err?.message || 'Failed to generate PDF.');
    } finally {
      setGenerating(false);
    }
  }, [engagementId, canGenerate, orderedStatements, orderedNotes, selectedReportIds, selectedNoteIds, mode, blobUrl]);

  const downloadName = `FSA-Financial-Statements-${mode}.pdf`;
  const handleDownload = useCallback(() => {
    if (!blobUrl) return;
    const a = document.createElement('a');
    a.href = blobUrl;
    a.download = downloadName;
    document.body.appendChild(a);
    a.click();
    a.remove();
    notificationService.toast.success('Download started.');
  }, [blobUrl, downloadName]);
  const handleOpenInTab = useCallback(() => {
    if (!blobUrl) return;
    const opened = window.open(blobUrl, '_blank');
    if (!opened) {
      notificationService.toast.warning(
        'Pop-up blocked. Allow pop-ups so the PDF can open in a new tab.', 6000,
      );
    }
  }, [blobUrl]);

  const allStmtChecked = orderedStatements.length > 0
    && orderedStatements.every(s => selectedReportIds.has(s.reportId));
  const allNoteChecked = orderedNotes.length > 0
    && orderedNotes.every(n => selectedNoteIds.has(n.scheduleId));

  return (
    <div style={screenStyle}>
      {/* Configuration pane (left) */}
      <aside style={configPaneStyle}>
        <header style={configHeaderStyle}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <IconSystem.print size={16} />
            <h2 style={{ margin: 0, fontSize: '1rem', fontWeight: 600 }}>Print Financial Statements</h2>
          </div>
          <p style={hintStyle}>
            Select Statements + Notes, choose display mode, then Generate. The PDF previews on the right.
          </p>
        </header>

        <div style={configBodyStyle}>
          {(stmtLoading || notesLoading) && (
            <div style={{ padding: '1rem', color: 'var(--text-secondary)' }}>Loading…</div>
          )}

          {!stmtLoading && !notesLoading && (
            <>
              <section style={sectionStyle}>
                <div style={sectionHeaderStyle}>
                  <label style={sectionTitleStyle}>
                    <input
                      type="checkbox"
                      checked={allStmtChecked}
                      onChange={e => toggleAllStatements(e.target.checked)}
                    />
                    <span>Financial Statements</span>
                    <span style={countStyle}>{selectedReportIds.size} / {orderedStatements.length}</span>
                  </label>
                </div>
                {orderedStatements.length === 0 && (
                  <div style={emptyStyle}>No statements configured for this engagement.</div>
                )}
                {orderedStatements.map(s => (
                  <label key={s.reportId} style={itemStyle}>
                    <input
                      type="checkbox"
                      checked={selectedReportIds.has(s.reportId)}
                      onChange={() => toggleReport(s.reportId)}
                    />
                    <span style={{ fontWeight: 500 }}>{s.reportName}</span>
                    <span style={subTextStyle}>{s.reportCode}</span>
                    {isStatementExcluded(s) && <span style={excludedChipStyle}>excluded</span>}
                    {!s.hasSavedData && <span style={notSavedChipStyle}>not saved</span>}
                  </label>
                ))}
              </section>

              <section style={sectionStyle}>
                <div style={sectionHeaderStyle}>
                  <label style={sectionTitleStyle}>
                    <input
                      type="checkbox"
                      checked={allNoteChecked}
                      onChange={e => toggleAllNotes(e.target.checked)}
                    />
                    <span>Notes to Accounts</span>
                    <span style={countStyle}>{selectedNoteIds.size} / {orderedNotes.length}</span>
                  </label>
                </div>
                {orderedNotes.length === 0 && (
                  <div style={emptyStyle}>
                    No notes available — all are marked "Do not include in note list" or none have been saved yet.
                  </div>
                )}
                {orderedNotes.map(n => (
                  <label key={n.scheduleId} style={itemStyle}>
                    <input
                      type="checkbox"
                      checked={selectedNoteIds.has(n.scheduleId)}
                      onChange={() => toggleNote(n.scheduleId)}
                    />
                    <span style={noteNumStyle}>
                      {n.noteNumber != null ? n.noteNumber : '—'}
                    </span>
                    <span style={{ fontWeight: 500 }}>{n.scheduleName}</span>
                    {!n.hasSavedData && <span style={notSavedChipStyle}>not saved</span>}
                  </label>
                ))}
              </section>

              <section style={sectionStyle}>
                <div style={sectionHeaderStyle}>
                  <span style={sectionTitleStyle}>Display Mode</span>
                </div>
                <div style={{ display: 'flex', gap: '1rem', padding: '0.5rem 0.875rem' }}>
                  <label style={radioStyle}>
                    <input type="radio" name="print-mode" checked={mode === 'actual'} onChange={() => setMode('actual')} />
                    <span>Actual</span>
                  </label>
                  <label style={radioStyle}>
                    <input type="radio" name="print-mode" checked={mode === 'rounded'} onChange={() => setMode('rounded')} />
                    <span>Rounded</span>
                  </label>
                </div>
              </section>
            </>
          )}
        </div>

        <footer style={configFooterStyle}>
          <button
            className="btn btn-primary"
            onClick={handleGenerate}
            disabled={!canGenerate}
            style={{ width: '100%' }}
          >
            <IconSystem.print size={16} />
            {generating ? 'Generating…' : 'Generate PDF'}
          </button>
          <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.5rem' }}>
            <button
              className="btn btn-secondary"
              onClick={handleOpenInTab}
              disabled={!blobUrl || !!error}
              style={{ flex: 1 }}
              title="Open in a new tab — use the browser's Print option from there"
            >
              <IconSystem.print size={14} /> Open / Print
            </button>
            <button
              className="btn btn-secondary"
              onClick={handleDownload}
              disabled={!blobUrl || !!error}
              style={{ flex: 1 }}
            >
              <IconSystem.download size={14} /> Download
            </button>
          </div>
        </footer>
      </aside>

      {/* Preview pane (right) */}
      <main style={previewPaneStyle}>
        {generating && (
          <div style={overlayStyle}>
            <div style={spinnerStyle} />
            <span style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', fontWeight: 500 }}>
              Rendering PDF…
            </span>
          </div>
        )}
        {error && !generating && (
          <div style={overlayStyle}>
            <IconSystem.alert size={28} />
            <div style={{ color: 'var(--text-primary)', fontWeight: 600 }}>Could not generate PDF</div>
            <div style={{ color: 'var(--text-secondary)', fontSize: '0.875rem', textAlign: 'center', maxWidth: 480 }}>
              {error}
            </div>
          </div>
        )}
        {!generating && !error && blobUrl && (
          <iframe
            src={`${blobUrl}#toolbar=1&navpanes=0`}
            title="FSA Consolidated PDF"
            style={{ flex: 1, width: '100%', height: '100%', border: 'none' }}
          />
        )}
        {!generating && !error && !blobUrl && (
          <div style={overlayStyle}>
            <IconSystem.file size={32} />
            <div style={{ color: 'var(--text-primary)', fontWeight: 600 }}>No preview yet</div>
            <div style={{ color: 'var(--text-secondary)', fontSize: '0.875rem', textAlign: 'center', maxWidth: 420 }}>
              Pick what to include on the left and click Generate PDF.
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

const screenStyle: React.CSSProperties = {
  display: 'flex',
  flex: 1,
  minHeight: 0,
  overflow: 'hidden',
};
const configPaneStyle: React.CSSProperties = {
  width: 360,
  flexShrink: 0,
  display: 'flex',
  flexDirection: 'column',
  borderRight: '1px solid var(--border-color)',
  background: 'var(--bg-primary)',
};
const configHeaderStyle: React.CSSProperties = {
  padding: '0.875rem 1rem 0.5rem',
  borderBottom: '1px solid var(--border-color)',
};
const hintStyle: React.CSSProperties = {
  margin: '0.25rem 0 0',
  fontSize: '0.75rem',
  color: 'var(--text-secondary)',
};
const configBodyStyle: React.CSSProperties = {
  flex: 1,
  overflow: 'auto',
  padding: '0.75rem',
};
const configFooterStyle: React.CSSProperties = {
  padding: '0.75rem 1rem',
  borderTop: '1px solid var(--border-color)',
  background: 'var(--bg-secondary)',
};
const sectionStyle: React.CSSProperties = {
  border: '1px solid var(--border-color)', borderRadius: 6,
  marginBottom: '0.75rem', overflow: 'hidden',
};
const sectionHeaderStyle: React.CSSProperties = {
  padding: '0.5rem 0.75rem', background: 'var(--bg-secondary)',
  borderBottom: '1px solid var(--border-color)',
};
const sectionTitleStyle: React.CSSProperties = {
  display: 'flex', alignItems: 'center', gap: '0.5rem',
  fontWeight: 600, fontSize: '0.85rem', cursor: 'pointer', userSelect: 'none',
};
const countStyle: React.CSSProperties = {
  marginLeft: 'auto', fontSize: '0.72rem', color: 'var(--text-secondary)', fontWeight: 500,
};
const itemStyle: React.CSSProperties = {
  display: 'flex', alignItems: 'center', gap: '0.5rem',
  padding: '0.35rem 0.75rem', fontSize: '0.85rem', cursor: 'pointer',
  borderBottom: '1px solid var(--border-color)', userSelect: 'none',
};
const noteNumStyle: React.CSSProperties = {
  display: 'inline-block', minWidth: 22, textAlign: 'right',
  fontVariantNumeric: 'tabular-nums', color: 'var(--text-secondary)', fontSize: '0.78rem',
};
const subTextStyle: React.CSSProperties = {
  marginLeft: 'auto', fontSize: '0.7rem', color: 'var(--text-secondary)',
};
const emptyStyle: React.CSSProperties = {
  padding: '0.5rem 0.875rem', color: 'var(--text-secondary)', fontSize: '0.82rem', fontStyle: 'italic',
};
const excludedChipStyle: React.CSSProperties = {
  marginLeft: 'auto', padding: '1px 6px', borderRadius: 999,
  background: '#f1f5f9', color: '#64748b',
  fontSize: '0.62rem', fontWeight: 700, letterSpacing: 0.3, textTransform: 'uppercase',
};
const notSavedChipStyle: React.CSSProperties = {
  padding: '1px 6px', borderRadius: 999,
  background: '#fef3c7', color: '#92400e',
  fontSize: '0.62rem', fontWeight: 700, letterSpacing: 0.3, textTransform: 'uppercase',
};
const radioStyle: React.CSSProperties = {
  display: 'inline-flex', alignItems: 'center', gap: '0.375rem',
  fontSize: '0.85rem', cursor: 'pointer', userSelect: 'none',
};
const previewPaneStyle: React.CSSProperties = {
  flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column',
  background: 'var(--bg-secondary)',
};
const overlayStyle: React.CSSProperties = {
  flex: 1, display: 'flex', flexDirection: 'column',
  alignItems: 'center', justifyContent: 'center',
  gap: '0.75rem', padding: '2rem',
};
const spinnerStyle: React.CSSProperties = {
  width: '2rem', height: '2rem',
  border: '3px solid var(--border-color)',
  borderTopColor: '#2563eb', borderRadius: '50%',
  animation: 'spin 0.8s linear infinite',
};

export default FsaConsolidatedPrintScreen;
